import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiFileText, FiX, FiCheck, FiClock } from 'react-icons/fi';
import axios from 'axios';
import { handleUnauthorized } from '../../utils/apiClient';
import { getToolboxToken, getAmsToken } from './QuoteStepper';
import { ROUTES } from '../../app/routes';
import type { PdfDataSnapshot } from './GeneratedProposal';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';

/* ── Inline SVG icons ── */
const svgBase = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const FiDownload: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const FiArrowRight: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

const FiUploadCloud: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const FiEdit2: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const FiEdit: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const FiEye: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const FiCopy: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);


/* ── Env URLs ── */


/* ── Status config ── */
const STATUS_CONFIG: Record<number, { label: string; bg: string; color: string; border: string; dot: string }> = {
  0: { label: 'Draft',     bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.40)',  color: '#fbbf24', dot: '#f59e0b' },
  1: { label: 'Generated', bg: 'rgba(16,185,129,0.10)',  border: 'rgba(16,185,129,0.40)',  color: '#34d399', dot: '#10b981' },
  2: { label: 'Viewed',    bg: 'rgba(139,92,246,0.10)',  border: 'rgba(139,92,246,0.40)',  color: '#a78bfa', dot: '#8b5cf6' },
  3: { label: 'Signed',    bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.40)',  color: '#60a5fa', dot: '#3b82f6' },
  4: { label: 'Expired',   bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.40)',   color: '#f87171', dot: '#ef4444' },
};

/* ── Date formatter ── */
const formatDate = (d: string) => {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

/* ── Component ── */
interface ProposalHistoryProps { onContinueDraft?: () => void; onCreateNew?: () => void; }

const ProposalHistory: React.FC<ProposalHistoryProps> = ({ onContinueDraft, onCreateNew }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [historyData, setHistoryData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  /* Edit title dialog */
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editProposalTitle, setEditProposalTitle] = useState('');
  const [editProposalId, setEditProposalId] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [titleError, setTitleError] = useState('');

  /* View / preview dialog */
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewRow, setPreviewRow] = useState<any>(null);

  /* PDF data snapshot from GeneratedProposal (for actual PDF generation) */
  const [pdfData, setPdfData] = useState<PdfDataSnapshot | null>(null);

  const handlePdfDataReady = useCallback((snapshot: PdfDataSnapshot) => {
    setPdfData(snapshot);
  }, []);

  /* Sign dialog */
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [signerEmail, setSignerEmail] = useState('');
  const [signerName, setSignerName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [signTarget, setSignTarget] = useState<any>(null);

  /* Loading state */
  const [actionLoading, setActionLoading] = useState<string>('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  /* GeneratedProposal lazy load */
  const [GeneratedProposal, setGeneratedProposal] = useState<React.ComponentType<any> | null>(null);
  useEffect(() => {
    import('./GeneratedProposal').then(m => setGeneratedProposal(() => m.default)).catch(() => {});
  }, []);

  const handle401 = useCallback((err: any) => {
    if (axios.isAxiosError(err) && err.response?.status === 401) handleUnauthorized();
  }, []);

  /* ── B. Get history ── */
  const getRecentProposalDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/nc-quote-proposal-history?origin=true`, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      const raw: any[] = res.data || [];
      const seen = new Set<string>();
      setHistoryData(
        raw
          .filter(item => {
            if (!item.transactionId || seen.has(item.transactionId)) return false;
            seen.add(item.transactionId);
            return true;
          })
          .map(item => ({
            fileName:      item.quoteApplicationPdf || 'NA',
            submittedDate: item.created || '',
            status:        Number(item.status ?? 0),
            isDraft:       Number(item.status ?? 0) === 0,
            transactionId: item.transactionId || '',
            clientName:    item.clientName || '',
            clientEmail:   item.ClientEmail || item.clientEmail || '',
            proposalTitle: item.proposalTitle || 'Untitled',
            clientId:      item.clientId || '',
            agentName:     item.agentName || '',
          }))
      );
    } catch (err) {
      handle401(err);
      showToast('error', 'Failed to load proposal history');
    } finally {
      setLoading(false);
    }
  }, [handle401, showToast]);

  /* ── C. Edit title ── */
  const handleEditProposalTitle = useCallback(async () => {
    if (!editProposalTitle.trim()) { setTitleError('Title cannot be empty'); return; }
    setEditLoading(true);
    try {
      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/updateProposalTitle`,
        null,
        {
          params: { transactionId: editProposalId, proposalTitle: editProposalTitle },
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        }
      );
      setEditDialogVisible(false);
      getRecentProposalDetails();
      showToast('success', 'Proposal title updated');
    } catch (err) {
      handle401(err);
      showToast('error', 'Failed to update title');
    } finally {
      setEditLoading(false);
    }
  }, [editProposalId, editProposalTitle, handle401, getRecentProposalDetails, showToast]);

  /* ── E. PDF generation using real pdfData from GeneratedProposal ── */
  const buildPdfBlobFromSnapshot = useCallback(async (): Promise<Blob> => {
    if (!pdfData) {
      throw new Error('PDF data not ready');
    }
    const { pdf } = await import('@react-pdf/renderer');
    const templateNum = pdfData.selectedTemplate || 3;

    const pdfProps = {
      title: pdfData.title,
      preparedFor: pdfData.preparedFor,
      preparedBy: pdfData.preparedBy,
      website: pdfData.website,
      logoURL: pdfData.logoURL,
      primaryColor: pdfData.primaryColor,
      secondaryColor: pdfData.secondaryColor,
      companyOverview: pdfData.companyOverview,
      aboutUs: pdfData.aboutUs,
      vision: pdfData.vision,
      mission: pdfData.mission,
      teamMembers: pdfData.teamMembers,
      proposalTeamMembers: pdfData.proposalTeamMembers,
      apiResponse: pdfData.apiResponse,
      proposalBreakdownDescriptions: pdfData.proposalBreakdownDescriptions,
      proposalComparisonDescriptions: pdfData.proposalComparisonDescriptions,
      documentTypeTitles: pdfData.documentTypeTitles,
      videoUrl: pdfData.videoUrl,
      acceptanceFormDescription: pdfData.acceptanceFormDescription,
      selectedTeamMember: pdfData.selectedTeamMember,
      selectedSignatureOption: pdfData.selectedSignatureOption,
      signatureImage: pdfData.signatureImage,
      extraDocumentList: pdfData.extraDocumentList,
      templateImages: pdfData.templateImages,
      dynamicLinks: pdfData.dynamicLinks || [],
    };

    let docEl: React.ReactElement;
    if (templateNum === 1) {
      const { default: ProposalDocumentT1 } = await import('./PDF/Template-1/Proposaldocumentt1');
      docEl = React.createElement(ProposalDocumentT1, pdfProps);
    } else if (templateNum === 2) {
      const { default: ProposalDocumentT2 } = await import('./PDF/Template-2/Proposaldocumentt2');
      docEl = React.createElement(ProposalDocumentT2, pdfProps);
    } else {
      const { default: ProposalDocumentMT } = await import('./PDF/Template-3/ProposalDocumentMT');
      docEl = React.createElement(ProposalDocumentMT, pdfProps);
    }
    return pdf(docEl as any).toBlob();
  }, [pdfData]);

  /* ── E2. Merge extra docs ── */
  const mergeWithExtraDocs = useCallback(async (mainBlob: Blob): Promise<Blob> => {
    try {
      const { PDFDocument } = await import('pdf-lib');
      const merged = await PDFDocument.create();
      const main = await PDFDocument.load(await mainBlob.arrayBuffer());
      const pages = await merged.copyPages(main, main.getPageIndices());
      pages.forEach(p => merged.addPage(p));

      const urlsToMerge = (pdfData?.extraDocumentList ?? [])
        .map((d: any) => d.FileURL)
        .filter(Boolean);

      for (const url of urlsToMerge) {
        try {
          const res = await fetch(url);
          const bytes = new Uint8Array(await res.arrayBuffer());
          const extra = await PDFDocument.load(bytes);
          const ep = await merged.copyPages(extra, extra.getPageIndices());
          ep.forEach(p => merged.addPage(p));
        } catch { /* skip failed */ }
      }

      const finalBytes = await merged.save();
      return new Blob([finalBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
    } catch {
      return mainBlob;
    }
  }, [pdfData]);

  /* ── F. Download (from preview dialog - uses pdfData) ── */
  const handleDownloadFromPreview = useCallback(async (row: any) => {
    if (!pdfData) {
      showToast('error', 'PDF data not ready. Please wait for preview to load.');
      return;
    }
    setActionLoading(`dl-${row.transactionId}`);
    setIsGeneratingPdf(true);
    try {
      const mainBlob = await buildPdfBlobFromSnapshot();
      const finalBlob = await mergeWithExtraDocs(mainBlob);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(finalBlob);
      link.download = `${row.proposalTitle || 'Proposal'}.pdf`;
      link.click();
      showToast('success', 'Download started');
    } catch (err) {
      console.error('PDF generation failed:', err);
      showToast('error', 'Failed to download PDF');
    } finally {
      setActionLoading('');
      setIsGeneratingPdf(false);
    }
  }, [pdfData, buildPdfBlobFromSnapshot, mergeWithExtraDocs, showToast]);

  /* ── G. Send to AMS (from preview dialog - uses pdfData) ── */
  const handleSendToAmsFromPreview = useCallback(async (row: any) => {
    if (!pdfData) {
      showToast('error', 'PDF data not ready. Please wait for preview to load.');
      return;
    }
    setActionLoading(`ams-${row.transactionId}`);
    setIsGeneratingPdf(true);
    try {
      const mainBlob = await buildPdfBlobFromSnapshot();
      const finalBlob = await mergeWithExtraDocs(mainBlob);
      const uid = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
      const file = new File([finalBlob], `Quote_Proposal_${uid}.pdf`, { type: 'application/pdf' });
      const fd = new FormData();
      fd.append('file', file); fd.append('fileName', file.name);
      fd.append('nc_token', getAmsToken()); fd.append('insuredId', row.clientId);
      fd.append('transactionId', row.transactionId);
      await axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/sendtoams`, fd, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      showToast('success', 'Sent to AMS successfully');
    } catch (err) { handle401(err); showToast('error', 'Failed to send to AMS'); }
    finally { setActionLoading(''); setIsGeneratingPdf(false); }
  }, [pdfData, buildPdfBlobFromSnapshot, mergeWithExtraDocs, handle401, showToast]);

  /* ── H. Sign (from preview dialog - uses pdfData) ── */
  const handleSignSubmit = useCallback(async () => {
    if (!pdfData) {
      showToast('error', 'PDF data not ready. Please wait for preview to load.');
      return;
    }
    if (!signerEmail.trim()) { setEmailError('Email is required'); return; }
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(signerEmail)) { setEmailError('Please enter a valid email'); return; }
    if (!signTarget) return;
    setActionLoading(`sign-${signTarget.transactionId}`);
    setIsGeneratingPdf(true);
    try {
      const mainBlob = await buildPdfBlobFromSnapshot();
      const finalBlob = await mergeWithExtraDocs(mainBlob);
      const uid = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
      const file = new File([finalBlob], `Quote_Proposal_${uid}.pdf`, { type: 'application/pdf' });
      const fd = new FormData();
      fd.append('file', file); fd.append('fileName', file.name);
      fd.append('nc_token', getAmsToken()); fd.append('insuredId', signTarget.clientId);
      fd.append('transactionId', signTarget.transactionId);
      const uploadRes = await axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/sendtoams`, fd, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      const databaseId = uploadRes.data?.data?.databaseId || uploadRes.data?.databaseId;
      const tokenRes = await axios.get(`${(process.env.REACT_APP_Nowcerts_Base_Url || '')}/api/IGToken/GetToken`, {
        headers: { Authorization: `Bearer ${getAmsToken()}` },
      });
      const igtoken = tokenRes.data;
      const fromEmail = localStorage.getItem('userName') || '';
      const url =
        `${(process.env.REACT_APP_RabbitSign_Url || 'https://test-bff-api.nowcerts.com')}/RabbitSignExternal` +
        `?igtoken=${igtoken}&theme=dark&fileId=${databaseId}&insuredId=${signTarget.clientId}` +
        `&signers=${encodeURIComponent(signerEmail)},${encodeURIComponent(signerName)},1` +
        `&fromEmail=${encodeURIComponent(fromEmail)}&emailSubject=Please%20sign%20this%20document&emailBody=Please%20sign%20this%20document`;
      window.open(url, '_blank');
      showToast('success', 'Document sent for signing');
    } catch (err) { handle401(err); showToast('error', 'Failed to initiate signing'); }
    finally { setActionLoading(''); setIsGeneratingPdf(false); setShowEmailDialog(false); }
  }, [pdfData, signerEmail, signerName, signTarget, buildPdfBlobFromSnapshot, mergeWithExtraDocs, handle401, showToast]);

  /* ── I2. Copy proposal link ── */
  const handleCopyLink = useCallback((row: any) => {
    const link = `${window.location.origin}/proposal-view/${row.transactionId}`;
    navigator.clipboard.writeText(link)
      .then(() => showToast('success', 'Proposal link copied to clipboard'))
      .catch(() => showToast('error', 'Failed to copy link'));
  }, [showToast]);

  /* ── I. Navigate to draft for editing ── */
  const handleOpenDraft = useCallback((row: any) => {
    onContinueDraft?.();
    navigate(`${ROUTES.QUOTE_PROPOSAL}/${row.transactionId}`);
  }, [navigate, onContinueDraft]);

  /* On mount */
  useEffect(() => { getRecentProposalDetails(); }, [getRecentProposalDetails]);

  /* Pagination */
  const PAGE_SIZE = 12;
  const [currentPage, setCurrentPage] = useState(1);

  /* Filtered data */
  const filtered = useMemo(() => {
    setCurrentPage(1);
    if (!globalFilter.trim()) return historyData;
    const q = globalFilter.toLowerCase();
    return historyData.filter(p =>
      p.proposalTitle?.toLowerCase().includes(q) ||
      p.clientName?.toLowerCase().includes(q) ||
      p.agentName?.toLowerCase().includes(q)
    );
  }, [historyData, globalFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  /* ── Render: main ── */
  return (
    <motion.div
      className="qp-history"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* Header */}
      <div className="qp-history__header">
        <h3 className="qp-history__title">Recent Proposals</h3>
        <div className="qp-history__search">
          <FiSearch size={13} />
          <input
            type="text"
            placeholder="Search proposals…"
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
          />
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="qp-history__body">

      {/* Table */}
      {loading ? (
        <div className="qp-history-loader" style={{ minHeight: 280 }}>
          <div className="qp-history-loader__orb">
            <div className="qp-history-loader__ring qp-history-loader__ring--1" />
            <div className="qp-history-loader__ring qp-history-loader__ring--2" />
            <div className="qp-history-loader__core"><FiClock size={16} /></div>
          </div>
          <div className="qp-history-loader__text">
            <p className="qp-history-loader__title">Loading Proposals</p>
            <p className="qp-history-loader__sub">Fetching your recent proposals<span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span></p>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="qp-empty-state">
          <FiFileText size={28} />
          <p>{globalFilter ? 'No proposals match your search' : 'No proposals yet. Create your first proposal!'}</p>
        </div>
      ) : (
        <div className="qp-table-wrap glass-surface">
          <table className="qp-table">
            <thead>
              <tr>
                <th className="qp-table__th">Proposal Title</th>
                <th className="qp-table__th">Client</th>
                <th className="qp-table__th">Date</th>
                <th className="qp-table__th">Status</th>
                <th className="qp-table__th" style={{ width: 200, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(p => {
                const status = STATUS_CONFIG[p.status] || STATUS_CONFIG[0];
                return (
                  <tr key={p.transactionId} className="qp-table__row">
                    <td className="qp-table__td qp-table__td--title">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{p.proposalTitle}</span>
                        <button
                          className="qp-table__edit-btn"
                          title="Edit title"
                          onClick={() => {
                            setEditProposalId(p.transactionId);
                            setEditProposalTitle(p.proposalTitle);
                            setTitleError('');
                            setEditDialogVisible(true);
                          }}
                        >
                          <FiEdit size={11} />
                        </button>
                      </div>
                    </td>
                    <td className="qp-table__td">{p.clientName || '-'}</td>
                    <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{formatDate(p.submittedDate)}</td>
                    <td className="qp-table__td">
                      <span
                        className="qp-status-pill"
                        style={{ color: status.color }}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="qp-table__td">
                      {(() => {
                        if (p.status === 0) {
                          return (
                            <button
                              className="qp-table__draft-link"
                              onClick={() => handleOpenDraft(p)}
                            >
                              Continue Draft <FiArrowRight size={12} />
                            </button>
                          );
                        }
                        return (
                          <div className="qp-table__actions">
                            <button
                              className="qp-table__action-btn"
                              title="View / Download / Send / Sign"
                              onClick={() => { setPreviewRow(p); setPreviewVisible(true); setPdfData(null); }}
                            >
                              <FiEye size={14} />
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="qp-pagination">
          <span className="qp-pagination__info">
            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="qp-pagination__controls">
            <button
              className="qp-pagination__btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              title="First"
            >«</button>
            <button
              className="qp-pagination__btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              title="Previous"
            >‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '…'
                  ? <span key={`ellipsis-${i}`} className="qp-pagination__ellipsis">…</span>
                  : <button
                      key={p}
                      className={`qp-pagination__btn${currentPage === p ? ' qp-pagination__btn--active' : ''}`}
                      onClick={() => setCurrentPage(p as number)}
                    >{p}</button>
              )
            }
            <button
              className="qp-pagination__btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              title="Next"
            >›</button>
            <button
              className="qp-pagination__btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Last"
            >»</button>
          </div>
        </div>
      )}

      </div>{/* end qp-history__body */}

      {/* ── Edit Title Dialog ── */}
      {editDialogVisible && (
        <div className="qp-dialog-overlay" onClick={() => setEditDialogVisible(false)}>
          <motion.div
            className="qp-dialog"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="qp-dialog__header">
              <h3>Edit Proposal Title</h3>
              <button className="qp-dialog__close" onClick={() => setEditDialogVisible(false)}><FiX size={18} /></button>
            </div>
            <div className="qp-dialog__body">
              <div className="qp-field">
                <label className="qp-field__label">Proposal Title</label>
                <input
                  type="text"
                  className={`qp-field__input${titleError ? ' qp-field__input--error' : ''}`}
                  value={editProposalTitle}
                  onChange={e => { setEditProposalTitle(e.target.value); setTitleError(''); }}
                  placeholder="Enter proposal title"
                />
                {titleError && <span className="qp-field__error">{titleError}</span>}
              </div>
            </div>
            <div className="qp-dialog__footer">
              <button className="glass-btn" onClick={() => setEditDialogVisible(false)}>Cancel</button>
              <button className="glass-btn glass-btn-primary" onClick={handleEditProposalTitle} disabled={editLoading}>
                {editLoading ? <div className="qp-spinner qp-spinner--small" /> : <FiCheck size={13} />} Save
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Preview Dialog ── */}
      {previewVisible && previewRow && (
        <div className="qp-dialog-overlay" onClick={() => { setPreviewVisible(false); setPdfData(null); }}>
          <motion.div
            className="qp-dialog qp-dialog--large"
            style={{ width: '90vw', maxWidth: 1100 }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="qp-dialog__header">
              <h3>{previewRow.proposalTitle}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="qp-pill-btn qp-pill-btn--outline" onClick={() => handleCopyLink(previewRow)} disabled={isGeneratingPdf} title="Copy proposal link">
                  <FiCopy size={11} /> Copy
                </button>
                <button className="qp-pill-btn qp-pill-btn--outline" onClick={() => handleDownloadFromPreview(previewRow)} disabled={isGeneratingPdf || !pdfData}>
                  <FiDownload size={11} /> Download
                </button>
                <button className="qp-pill-btn qp-pill-btn--outline" onClick={() => handleSendToAmsFromPreview(previewRow)} disabled={isGeneratingPdf || !pdfData}>
                  <FiUploadCloud size={11} /> Send to AMS
                </button>
                <button
                  className="qp-pill-btn qp-pill-btn--outline"
                  disabled={isGeneratingPdf || !pdfData}
                  onClick={() => { setPreviewVisible(false); setSignTarget(previewRow); setSignerEmail(previewRow.clientEmail || ''); setSignerName(previewRow.clientName || ''); setEmailError(''); setShowEmailDialog(true); }}
                  title="Sign document"
                >
                  <FiEdit2 size={11} /> Sign Document
                </button>
                <button className="qp-dialog__close" onClick={() => { setPreviewVisible(false); setPdfData(null); }}><FiX size={18} /></button>
              </div>
            </div>
            <div className="qp-dialog__body" style={{ padding: 'var(--space-4)' }}>
              {GeneratedProposal ? (
                <GeneratedProposal transactionId={previewRow.transactionId} isEditable={true} isGeneratingPdf={false} showBottomActions={false} onPdfDataReady={handlePdfDataReady} />
              ) : (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <FiFileText size={36} style={{ color: '#6366f1', marginBottom: 12 }} />
                  <p style={{ fontWeight: 600 }}>{previewRow.proposalTitle}</p>
                  <p style={{ fontSize: 13, color: '#64748b' }}>Prepared for {previewRow.clientName}</p>
                </div>
              )}
            </div>
            <div className="qp-dialog__footer">
              <button className="qp-pill-btn qp-pill-btn--outline" onClick={() => { setPreviewVisible(false); if (onCreateNew) { onCreateNew(); } else { navigate(ROUTES.QUOTE_PROPOSAL); } }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Create New
              </button>
              <button className="qp-pill-btn qp-pill-btn--outline" onClick={() => { window.open(`/proposal-view/${previewRow.transactionId}`, '_blank', 'noopener,noreferrer'); }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                Share Proposal
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Sign Dialog ── */}
      {showEmailDialog && (
        <div className="qp-dialog-overlay" onClick={() => setShowEmailDialog(false)}>
          <motion.div
            className="qp-dialog"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="qp-dialog__header">
              <h3>Sign Document</h3>
              <button className="qp-dialog__close" onClick={() => setShowEmailDialog(false)}><FiX size={18} /></button>
            </div>
            <div className="qp-dialog__body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
                The document will be sent to this email for e-signature.
              </p>
              <div className="qp-field">
                <label className="qp-field__label">Email *</label>
                <input
                  type="email"
                  className={`qp-field__input${emailError ? ' qp-field__input--error' : ''}`}
                  placeholder="Enter signer email"
                  value={signerEmail}
                  onChange={e => { setSignerEmail(e.target.value); setEmailError(''); }}
                />
                {emailError && <span className="qp-field__error">{emailError}</span>}
              </div>
            </div>
            <div className="qp-dialog__footer">
              <button className="glass-btn" onClick={() => setShowEmailDialog(false)} disabled={isGeneratingPdf}>Cancel</button>
              <button className="glass-btn glass-btn-primary" onClick={handleSignSubmit} disabled={isGeneratingPdf}>
                {isGeneratingPdf ? <div className="qp-spinner qp-spinner--small" /> : <FiCheck size={13} />} Confirm &amp; Sign
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default ProposalHistory;

